/**
 * Return focus to whatever opened a dialog once it has closed (WCAG 2.4.3).
 *
 * Used by the shared `Modal` and `Drawer` primitives. Headless UI 1.7's own
 * restore does not fire when its Dialog unmounts at the end of a
 * `TransitionRoot` leave (its focus trap is already marked disabled by then),
 * so after an Esc / Cancel / overlay / × close focus was left on `<body>` and
 * keyboard users lost their place.
 *
 * - The opener is captured synchronously when `open` turns true — before
 *   Headless UI moves focus into the panel — or at setup when the dialog
 *   mounts already open.
 * - `restore()` is called by the primitive from the TransitionRoot
 *   `after-leave`; it waits for the panel to unmount, then focuses the
 *   explicit `returnFocusTo` target if that is usable, else the opener —
 *   provided it is still in the document and focusable (not disabled, not
 *   hidden, not inside an `inert` subtree). An opener the page re-rendered
 *   meanwhile is matched to its replacement by a unique `id` / `data-testid`.
 * - It never steals focus from another dialog that holds focus by then
 *   (focus is inside a live `[role="dialog"]` that does not contain the target).
 * - Unmounted while still open (consumer `v-if`), it restores the same way.
 */
import { nextTick, onBeforeUnmount, watch } from 'vue';

/**
 * Whether `el` can take focus back.
 * @param el candidate element.
 * @returns true when it is a connected, enabled, visible, non-inert element.
 */
export function isFocusableTarget(el: unknown): el is HTMLElement {
  if (typeof HTMLElement === 'undefined' || !(el instanceof HTMLElement)) return false;
  if (!el.isConnected || el === el.ownerDocument.body) return false;
  if ((el as HTMLElement & { disabled?: boolean }).disabled === true) return false;
  if (el.closest('[inert], [hidden]')) return false;
  return true;
}

/**
 * The opener's stand-in when the page re-rendered it while the dialog was open
 * (e.g. a list page whose toolbar re-mounts after its first load): the single
 * element now carrying the same `id` or `data-testid`, if focusable.
 * @param el the captured (now detached) opener.
 * @returns the replacement element, or null when there is no unique match.
 */
function reRenderedOpener(el: HTMLElement | null): HTMLElement | null {
  if (!el || el.isConnected) return null;
  const doc = el.ownerDocument;
  const pick = (selector: string): HTMLElement | null => {
    const matches = doc.querySelectorAll<HTMLElement>(selector);
    return matches.length === 1 && isFocusableTarget(matches[0]) ? matches[0]! : null;
  };
  if (el.id) return pick(`#${CSS.escape(el.id)}`);
  const testId = el.getAttribute('data-testid');
  if (testId) return pick(`[data-testid="${CSS.escape(testId)}"]`);
  return null;
}

/** Let Headless UI unmount the panel (and drop its focus lock) first. */
async function afterDialogTeardown(): Promise<void> {
  await nextTick();
  await new Promise<void>((resolve) => setTimeout(resolve, 0));
}

/**
 * @param isOpen getter for the dialog's `open` prop.
 * @param explicitTarget getter for an optional element that should receive
 *   focus instead of the opener (the primitives' `returnFocusTo` prop).
 * @returns `restore` — call it from the TransitionRoot `after-leave`; it
 *   resolves once focus has been handed back (or there was nothing to do).
 */
export function useDialogReturnFocus(
  isOpen: () => boolean,
  explicitTarget: () => HTMLElement | null | undefined,
): { restore: () => Promise<void> } {
  let opener: HTMLElement | null = null;
  let armed = false;

  /** Remember the element focused right now as the opener. */
  function capture(): void {
    if (typeof document === 'undefined') return;
    const active = document.activeElement;
    opener = active instanceof HTMLElement && active !== document.body ? active : null;
    armed = true;
  }

  if (isOpen()) capture();
  // `sync` so the opener is read before Headless UI focuses into the panel.
  watch(
    isOpen,
    (open) => {
      if (open) capture();
    },
    { flush: 'sync' },
  );

  /** Focus the explicit target / opener now (the dialog is already gone). */
  function focusBack(): void {
    if (!armed) return;
    armed = false;
    const fromOpener = opener;
    opener = null;
    if (typeof document === 'undefined') return;
    const explicit = explicitTarget();
    const target = isFocusableTarget(explicit)
      ? explicit
      : isFocusableTarget(fromOpener)
        ? fromOpener
        : reRenderedOpener(fromOpener);
    if (!target) return;
    const active = document.activeElement;
    const liveDialog =
      active instanceof HTMLElement ? active.closest<HTMLElement>('[role="dialog"]') : null;
    // A different dialog holds focus (opened meanwhile) — leave it alone.
    if (liveDialog && !liveDialog.contains(target)) return;
    target.focus({ preventScroll: true });
  }

  /**
   * Hand focus back once the dialog is out of the DOM. `after-leave` fires
   * while the panel — and Headless UI's focus lock, which would pull focus
   * straight back in — is still mounted, so wait for the teardown first.
   */
  async function restore(): Promise<void> {
    if (!armed) return;
    await afterDialogTeardown();
    // Re-opened meanwhile: that open re-captured the opener; nothing to do.
    if (isOpen()) return;
    focusBack();
  }

  // Unmounted while still open: no leave transition runs. `isOpen()` still
  // reports the last prop here, so skip the open check.
  onBeforeUnmount(() => {
    if (!armed || !isOpen()) return;
    void afterDialogTeardown().then(focusBack);
  });

  return { restore };
}
